/**
 * Reader labels for the deterministic water-use screen's enum values.
 *
 * The screen stores machine tokens (`onsite-direct-water`, `selected-technology`,
 * `wet-cooling-tower`, …). The saved "Modeled Water Use" section, and the prompt
 * that shares its builder, printed those tokens verbatim. Every map here is an
 * exhaustive `Record<Union, string>`, so a new enum member fails the type check
 * until it is given a label rather than leaking a raw token into a report.
 *
 * Two distinctions are load-bearing and must never share a label (pinned in
 * `__tests__/waterScreeningLabels.test.ts`): withdrawal vs consumption, and a
 * published benchmark vs a technology sensitivity. Every applicability label
 * also carries "benchmark", "screening" or "modeled": it is the qualifier that
 * satisfies WATER-TRUTH-1's line-level caveat check on each scenario row.
 */
import type {
  DataCenterOperatingModel,
  DataCenterWorkload,
  PowerMwBasis,
  PowerMwBasisTreatment,
  ScreeningInputSource,
  WaterUseDryCoolingCapacityStatus,
  WaterUseDryCoolingDesignScreen,
  WaterUseFlowBasis,
  WaterUseScenarioKind,
  WaterUseScenarioResult,
} from '../../types/economicContribution';
import type { BopHeatRejection, ProjectIntent } from '../../types/siteRag';
import type { TmyDataset } from '../../types/tmy';

/** What volume a row measures. */
export const WATER_FLOW_BASIS_LABELS: Readonly<Record<WaterUseFlowBasis, string>> = {
  withdrawal: 'withdrawal',
  consumption: 'consumption',
  'cooling-water': 'cooling water',
  unspecified: 'withdrawal or consumption not specified',
};

/** Which part of the site a row covers. */
export const WATER_BOUNDARY_LABELS: Readonly<Record<WaterUseScenarioResult['boundary'], string>> = {
  'onsite-direct-water': 'whole site',
  'cooling-process-only': 'cooling process only',
};

/** How a row relates to this site. Each label carries its own screening qualifier. */
export const WATER_APPLICABILITY_LABELS: Readonly<Record<WaterUseScenarioResult['applicability'], string>> = {
  benchmark: 'published benchmark',
  'selected-technology': 'modeled for the recorded heat rejection',
  sensitivity: 'modeled technology sensitivity',
};

/** The heat-rejection technology a scenario kind stands for; null for a whole-site benchmark. */
export const WATER_SCENARIO_TECHNOLOGY_LABELS: Readonly<Record<WaterUseScenarioKind, string | null>> = {
  'modern-operator-fleet': null,
  'national-current-average': null,
  'wet-cooling-tower-consumption': 'wet cooling tower',
  'wet-cooling-tower-withdrawal': 'wet cooling tower',
  'dry-cooling': 'dry cooling',
};

export const HEAT_REJECTION_LABELS: Readonly<Record<BopHeatRejection, string>> = {
  'dry-cooler': 'dry cooler',
  'wet-cooling-tower': 'wet cooling tower',
  hybrid: 'hybrid wet/dry',
  'once-through': 'once-through cooling',
  'absorption-cchp': 'absorption chilling (CCHP)',
  'free-cooling': 'free cooling',
  // The model's own framing ("Heat rejection is unknown"); an absence framing such as
  // "not yet recorded" reads as an R-131 absence assertion the section cannot cite.
  unknown: 'unknown',
};

export const DATA_CENTER_WORKLOAD_LABELS: Readonly<Record<DataCenterWorkload, string>> = {
  'general-cloud': 'general cloud',
  'ai-hpc': 'AI training / HPC',
  inference: 'AI inference',
  mixed: 'mixed',
  unknown: 'not recorded',
};

export const DATA_CENTER_OPERATING_MODEL_LABELS: Readonly<Record<DataCenterOperatingModel, string>> = {
  hyperscale: 'hyperscale',
  colocation: 'colocation',
  enterprise: 'enterprise',
  'shell-lease': 'shell lease',
  unknown: 'not recorded',
};

export const IT_COOLING_LOOP_LABELS: Readonly<Record<NonNullable<ProjectIntent['coolingStrategy']>, string>> = {
  air: 'air',
  liquid: 'liquid',
  hybrid: 'hybrid air/liquid',
  undecided: 'undecided',
};

export const POWER_MW_BASIS_LABELS: Readonly<Record<PowerMwBasis, string>> = {
  it: 'IT load',
  facility: 'facility load',
  'utility-service': 'utility-service load',
};

export const POWER_MW_BASIS_TREATMENT_LABELS: Readonly<Record<PowerMwBasisTreatment, string>> = {
  'as-recorded': 'as recorded',
  'facility-as-utility-service-screening-proxy': 'facility load used as a utility-service screening proxy',
  'unspecified-as-facility-as-utility-service-screening-proxy': 'unspecified basis treated as facility load for a utility-service screening proxy',
  'it-pue-to-facility-as-utility-service-screening-proxy': 'IT load converted with PUE to facility load for a utility-service screening proxy',
};

export const SCREENING_INPUT_SOURCE_LABELS: Readonly<Record<ScreeningInputSource, string>> = {
  'site-record': 'site record',
  'screening-assumption': 'screening assumption',
};

export const DRY_COOLING_CAPACITY_STATUS_LABELS: Readonly<Record<WaterUseDryCoolingCapacityStatus, string>> = {
  'dry-only-screening-candidate': 'dry-only screening candidate',
  'vendor-selection-required': 'vendor selection required',
  'assist-or-trim-required': 'assist or trim cooling required',
  'reference-temperature-exceeded': 'reference temperature exceeded',
};

/**
 * Display labels for the selected screening wet bulb. The `'noaa-design'` key
 * is a legacy persisted identifier: the value is a NOAA normals estimate
 * (monthly mean temperature at an assumed RH), not a design condition.
 * `'tmy-coincident'` appears only on the dry-cooling screen.
 */
export const WET_BULB_BASIS_LABELS: Readonly<Record<WaterUseDryCoolingDesignScreen['wetBulbBasis'], string>> = {
  'noaa-design': 'NOAA-normals estimated',
  'sed-peak-proxy': 'SED peak-proxy',
  'tmy-coincident': 'TMY hottest-hours coincident',
};

export const TMY_SOURCE_LABELS: Readonly<Record<TmyDataset['source'], string>> = {
  'nrel-psm3': 'NREL NSRDB PSM3',
  pvgis: 'EU JRC PVGIS',
  'ashrae-iwec2': 'ASHRAE IWEC2',
};
